import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { DatePipe, UpperCasePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import {
	ItCardComponent,
	ItIconComponent,
	ItButtonDirective,
	ItProgressButtonComponent,
	ItAccordionComponent,
	ItModalComponent,
	ItInputComponent,
	ItSelectComponent,
	ItCheckboxComponent,
	UploadFileListItem,
	ItFileUtils,
} from 'design-angular-kit';
import { firstValueFrom, switchMap } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FilesComponent } from '../files/files.component';
import { IterlexService } from '../iterlex.service';
import { environment } from './../../environments/environment';
import type { Authority } from '../interfaces/authority';
import type { Phase } from '../interfaces/phase';
import type { Document } from '../interfaces/document';
import type { DocumentComplete } from '../interfaces/document-complete';
import type { DocumentType } from '../interfaces/document-type';
import type { Dossier } from '../interfaces/dossier';
import type { Step } from '../interfaces/step';
import { HttpParams } from '@angular/common/http';

@Component({
	selector: 'app-document',
	imports: [
		FilesComponent,
		FormsModule,
		ItCardComponent,
		ItButtonDirective,
		ItProgressButtonComponent,
		ItIconComponent,
		ItAccordionComponent,
		ItModalComponent,
		ItInputComponent,
		ItSelectComponent,
		ItCheckboxComponent,
		TranslateModule,
		DatePipe,
		UpperCasePipe,
	],
	templateUrl: './document.component.html',
	styleUrl: './document.component.scss',
})
export class DocumentComponent {
	private activatedRoute = inject(ActivatedRoute);
	private translateService = inject(TranslateService);
	private iterlexService = inject(IterlexService);

	@Input({ required: true }) dossier!: Dossier;
	@Input({ required: true }) currentPhase!: Phase;
	@Input({ required: true }) currentStep!: Step;
	@Input({ required: true }) authority!: Authority;
	@Input({ required: true }) body!: Authority;
	@Input({ required: true }) documentTypes!: DocumentType[];
	@Input() progressDeleteDocument = false;
	@Input({ required: true }) set document(document: Document) {
		this.iterlexService
			.getDocumentComplete(
				this.authority.id,
				this.body.id,
				document.workUri,
				this.currentPhase.id,
				this.currentStep.id,
				this.dossier.workUri,
			)
			.subscribe((documentComplete) => {
				for (const version of documentComplete.versions) {
					this.uploadedFileList[version.expressionUri] = [];
				}
				this.documentComplete = documentComplete;
				this.attachments = [];
				for (const component of documentComplete.components) {
					this.iterlexService
						.getDocumentComplete(
							this.authority.id,
							this.body.id,
							component.childUri,
							this.currentPhase.id,
							this.currentStep.id,
							this.dossier.workUri,
						)
						.subscribe((documentComplete) => {
							this.attachments.push(documentComplete);
						});
				}
				const mainAliasAuthor = this.iterlexService
					.getMainAlias(documentComplete, this.body.id)
					.uri.split('/')[5];
				this.isFromDifferentAuthority =
					mainAliasAuthor != this.body.id && mainAliasAuthor != 'pab';
			});
	}
	@Output() setCurrentPhaseEvent = new EventEmitter<string>();
	@Output() deleteDocument = new EventEmitter<Document>();
	@Output() refreshDocuments = new EventEmitter();
	uploadedFileList: Record<string, UploadFileListItem[]> = {};
	documentComplete!: DocumentComplete;
	attachments: Document[] = [];
	phases: Phase[] = [];
	steps: Record<string, Step[]> = {};
	currentSteps: string[] = [];
	currentExpressionUri = '';
	isFromDifferentAuthority = false;
	progressDeleteVersionUri = '';
	currentFile!: File;
	languages = environment.documentLanguages;
	selectedSteps = new Set<string>();
	deselectedSteps = new Set<string>();
	getDocumentNumber() {
		return this.iterlexService.getMainAlias(
			this.documentComplete,
			this.body.id,
		).number;
	}
	getDocumentTypeLabel(uri?: string) {
		for (const documentType of this.documentTypes) {
			const subtype = documentType.subtypes.find(
				(subtype) =>
					subtype.id ==
					(
						uri ??
						this.iterlexService.getMainAlias(
							this.documentComplete,
							this.body.id,
						).uri
					).split('/')[4],
			);
			if (subtype) {
				return subtype.label[
					this.translateService.getCurrentLang() ??
						this.translateService.getFallbackLang()
				];
			}
		}
		return '';
	}
	updateDocumentComplete() {
		this.iterlexService
			.getDocumentComplete(
				this.authority.id,
				this.body.id,
				this.documentComplete.workUri,
				this.currentPhase.id,
				this.currentStep.id,
				this.dossier.workUri,
			)
			.subscribe((documentComplete) => {
				this.documentComplete = documentComplete;
				this.attachments = [];
				for (const component of documentComplete.components) {
					this.iterlexService
						.getDocumentComplete(
							this.authority.id,
							this.body.id,
							component.childUri,
							this.currentPhase.id,
							this.currentStep.id,
							this.dossier.workUri,
						)
						.subscribe((documentComplete) => {
							this.attachments.push(documentComplete);
						});
				}
			});
	}
	onEditDocumentSubmit(form: NgForm) {
		if (form.valid) {
			this.iterlexService
				.editDocument(
					this.documentComplete.workUri,
					this.authority.id,
					this.body.id,
					form.value.title,
				)
				.subscribe(() => {
					this.documentComplete.title = form.value.title;
				});
		}
	}
	setCurrentFile(files: FileList | null) {
		if (files) {
			this.currentFile = files[0];
		}
	}
	onCreateVersionSubmit(form: NgForm) {
		if (form.valid) {
			this.iterlexService
				.createVersion(
					this.documentComplete.workUri,
					this.iterlexService.getMainAlias(
						this.documentComplete,
						this.body.id,
					).uri,
					this.iterlexService.getMainAlias(
						this.documentComplete,
						this.body.id,
					).number,
					this.authority.id,
					this.body.id,
					form.value.language,
					this.documentComplete.title,
					this.iterlexService.dateTimeWithTimeZone(
						form.value.date,
						form.value.time,
					),
					this.iterlexService
						.getMainAlias(this.documentComplete, this.body.id)
						.uri.split('/')[5],
				)
				.subscribe((version) => {
					this.iterlexService
						.setVersionStep(
							this.authority.id,
							this.body.id,
							version.expressionUri,
							this.currentPhase.id,
							this.currentStep.id,
						)
						.subscribe(() => {
							this.uploadedFileList[version.expressionUri] = [];
							this.documentComplete.versions.push({
								...version,
								files: [],
							});
							this.documentComplete.versions.sort((a, b) => {
								return (
									new Date(a.date).getTime() -
									new Date(b.date).getTime()
								);
							});
							if (form.value.file) {
								ItFileUtils.fileToBase64(this.currentFile)
									.pipe(
										switchMap((base64) => {
											return this.iterlexService.createFile(
												version.expressionUri,
												this.authority.id,
												this.body.id,
												this.currentFile.name,
												version.date,
												this.body.id,
												base64,
											);
										}),
									)
									.subscribe(() =>
										this.refreshDocuments.emit(),
									);
							}
						});
				});
		}
	}
	onCreateAttachmentSubmit(form: NgForm) {
		if (form.valid) {
			const [documentType, documentSubtype] =
				form.value.documentSubtype.split('/');
			this.iterlexService
				.createDocument(
					this.authority.id,
					this.body.id,
					documentType,
					documentSubtype,
					this.documentComplete.author,
					form.value.date,
					form.value.number,
					form.value.title,
					this.dossier.workUri,
					'attachment',
				)
				.subscribe((document) => {
					this.iterlexService
						.createVersion(
							document.workUri,
							document.workUri,
							form.value.number,
							this.authority.id,
							this.body.id,
							form.value.language,
							document.title,
							this.iterlexService.dateTimeWithTimeZone(
								form.value.date,
								form.value.time,
							),
							document.author,
						)
						.subscribe((version) => {
							this.iterlexService
								.setVersionStep(
									this.authority.id,
									this.body.id,
									version.expressionUri,
									this.currentPhase.id,
									this.currentStep.id,
								)
								.subscribe(() => {
									this.iterlexService
										.createComponent(
											this.authority.id,
											this.body.id,
											this.dossier.workUri,
											this.documentComplete.workUri,
											document.workUri,
										)
										.subscribe(() =>
											this.updateDocumentComplete(),
										);
								});
						});
				});
		}
	}
	deleteVersion(expressionUri: string) {
		this.progressDeleteVersionUri = expressionUri;
		this.iterlexService
			.getVersionSteps(expressionUri, this.authority.id, this.body.id)
			.subscribe((steps) => {
				if (steps.length == 1) {
					this.iterlexService
						.getVersions(
							new HttpParams()
								.append(
									'workUri',
									this.documentComplete.workUri,
								)
								.append('authority', this.authority.id)
								.append('body', this.body.id),
						)
						.subscribe((versions) => {
							if (versions.length == 1) {
								this.iterlexService
									.deleteDocument(
										this.documentComplete.workUri,
										this.authority.id,
										this.body.id,
									)
									.subscribe({
										next: () => {
											this.refreshDocuments.emit();
										},
										complete: () => {
											this.progressDeleteVersionUri = '';
										},
									});
							} else {
								this.iterlexService
									.deleteVersion(
										expressionUri,
										this.authority.id,
										this.body.id,
									)
									.subscribe({
										next: () => {
											this.updateDocumentComplete();
										},
										complete: () => {
											this.progressDeleteVersionUri = '';
										},
									});
							}
						});
				} else {
					this.iterlexService
						.deleteVersionStep(
							expressionUri,
							this.authority.id,
							this.body.id,
							this.currentStep.id,
						)
						.subscribe({
							next: () => {
								this.updateDocumentComplete();
							},
							complete: () => {
								this.progressDeleteVersionUri = '';
							},
						});
				}
			});
	}
	getPhasesAndSteps() {
		const dossierUri =
			this.activatedRoute.snapshot.queryParamMap.get('uri');
		if (dossierUri) {
			this.steps = {};
			this.selectedSteps.clear();
			this.deselectedSteps.clear();
			this.iterlexService
				.getDossier(this.authority.id, this.body.id, dossierUri)
				.subscribe((dossier) => {
					this.iterlexService
						.getPhases(
							this.authority.id,
							this.body.id,
							dossier.type,
						)
						.subscribe((phases) => {
							this.phases = phases;
							for (const phase of phases) {
								this.iterlexService
									.getSteps(
										this.authority.id,
										this.body.id,
										dossier.workUri,
										phase.id,
									)
									.subscribe((steps) => {
										this.steps[phase.id] = steps;
									});
							}
						});
				});
		}
	}
	getVersionSteps(expressionUri: string) {
		this.iterlexService
			.getVersionSteps(expressionUri, this.authority.id, this.body.id)
			.subscribe((steps) => {
				this.currentSteps = steps;
			});
	}
	changeStep(phaseId: string, stepId: string) {
		if (stepId == this.currentStep.id) {
			return;
		}
		const id = phaseId + '/' + stepId;
		if (this.currentSteps.includes(stepId)) {
			if (this.deselectedSteps.has(id)) {
				this.deselectedSteps.delete(id);
			} else {
				this.deselectedSteps.add(id);
			}
		} else {
			if (this.selectedSteps.has(id)) {
				this.selectedSteps.delete(id);
			} else {
				this.selectedSteps.add(id);
			}
		}
	}
	async onMoveVersionSubmit(form: NgForm) {
		if (form.valid) {
			const selectedPhases = new Set<string>();
			for (const selectedStep of this.selectedSteps) {
				const [phaseId, stepId] = selectedStep.split('/');
				await firstValueFrom(
					this.iterlexService.setVersionStep(
						this.authority.id,
						this.body.id,
						this.currentExpressionUri,
						phaseId,
						stepId,
					),
				);
				selectedPhases.add(phaseId);
			}
			for (const deselectedStep of this.deselectedSteps) {
				const [, stepId] = deselectedStep.split('/');
				await firstValueFrom(
					this.iterlexService.deleteVersionStep(
						this.currentExpressionUri,
						this.authority.id,
						this.body.id,
						stepId,
					),
				);
			}
			const lastPhase = this.phases
				.filter((phase) => selectedPhases.has(phase.id))
				.pop();
			if (lastPhase) {
				this.setCurrentPhaseEvent.emit(lastPhase.id);
			}
		}
	}
	onCreateAliasSubmit(form: NgForm) {
		const dossierUri =
			this.activatedRoute.snapshot.queryParamMap.get('uri');
		if (form.valid && dossierUri) {
			const [documentType, documentSubtype] =
				form.value.documentSubtype.split('/');
			this.iterlexService
				.createDocumentAlias(
					this.authority.id,
					this.body.id,
					this.documentComplete.workUri,
					documentType,
					documentSubtype,
					dossierUri.split('/')[5],
					form.value.date,
					form.value.number,
				)
				.subscribe((alias) => {
					console.log(alias);
					this.iterlexService
						.editDocument(
							this.documentComplete.workUri,
							this.authority.id,
							this.body.id,
							this.documentComplete.title,
							alias.uri,
						)
						.subscribe(() => {
							this.refreshDocuments.emit();
						});
				});
		}
	}
}
